import * as _ from 'underscore';

/**
 * The interface required by the data grid body
 */
export interface IColumnWidths {
  getActual(index: number): number;
  getMin?: (index: number) => number;
  hasPreferredWidths?: () => boolean;
  resetActual?: () => void;
}

export class ColumnWidths implements IColumnWidths {

  private static MaxStartWidth: number = 200;

  public resizer = new ColumnWidthsResizer(this);

  private minWidths: number[];
  private preferredWidths: number[];
  private containerWidth: number;
  private actualWidths: number[] = [];
  private totalActualWidth: number;

  public getTotal(): number {
    return this.totalActualWidth || null;
  }

  public updateTotal() {
    this.totalActualWidth = _.reduce(this.actualWidths, (total: number, width: number) => total + width, 0);
  }

  public getActual(index: number): number {
    return this.actualWidths[index];
  }

  public setActual(index: number, width: number) {
    this.actualWidths[index] = width;
  }

  public resetActual() {
    this.actualWidths = [];
  }

  public getMin(index: number): number {
    return this.minWidths && this.minWidths[index];
  }

  public setMinWidths(minWidths: number[]) {
    this.minWidths = minWidths;
    this.maybeExpand();
  }

  public hasMinWidths() {
    return !!this.minWidths;
  }

  // Minimum overrides preferred.
  public getPreferred(index: number): number {
    const preferred = this.preferredWidths && this.preferredWidths[index];
    const min = this.getMin(index);
    return _.isNumber(preferred)
      ? (_.isNumber(min) ? Math.max(preferred, min) : preferred)
      : min;
  }

  // The given maximum overrides preferred.  Minimum overrides both.
  private getPreferredWithMax(index: number, max: number): number {
    const preferred = this.preferredWidths && this.preferredWidths[index];
    const min = this.getMin(index);
    if (_.isNumber(preferred)) {
      const maxPreferred = Math.min(preferred, max);
      return _.isNumber(min) ? Math.max(maxPreferred, min) : maxPreferred;
    } else {
      return min;
    }
  }

  public setPreferredWidths(preferredWidths: number[], containerWidth?: number) {
    this.preferredWidths = preferredWidths;
    this.containerWidth = containerWidth;
    this.maybeExpand();
  }

  public hasPreferredWidths() {
    return !!this.preferredWidths;
  }

  public setContainerWidth(containerWidth: number) {
    this.containerWidth = containerWidth;
    this.maybeExpand();
  }

  public hasContainerWidth() {
    return !!this.containerWidth;
  }

  private maybeExpand() {
    if (this.hasMinWidths() && this.hasPreferredWidths() && this.hasContainerWidth()) {
      this.expandToFill(this.containerWidth);
    }
  }

  private expandToFill(availableWidth: number) {
    // First, set the widths to the preferred, subject to the minimum widths and maximum starting width.
    const numColumns = Math.max(
      this.minWidths && this.minWidths.length || 0,
      this.preferredWidths && this.preferredWidths.length || 0);
    let totalWidth = 0;
    _.times(numColumns, (index: number) => {
      const width = this.getPreferredWithMax(index, ColumnWidths.MaxStartWidth);
      this.setActual(index, width);
      totalWidth += width;
    });
    // Then, distribute any extra width in left-to-right order.
    let extraWidth = availableWidth - totalWidth;
    if (extraWidth > 0) {
      _.each(this.actualWidths, (actualWidth: number, index: number) => {
        const preferredWidth = this.getPreferred(index);
        if (extraWidth > 0 && actualWidth < preferredWidth) {
          const usedWidth = Math.min(preferredWidth - actualWidth, extraWidth);
          this.setActual(index, actualWidth + usedWidth);
          extraWidth -= usedWidth;
        }
      });
    }
    this.updateTotal();
  }

}

export class ColumnWidthsResizer {

  private widths: ColumnWidths;

  private index: number;
  private startX: number;
  private startWidth: number;
  private minWidth: number;

  constructor(widths: ColumnWidths) {
    this.widths = widths;
  }

  public startResize(index: number, x: number) {
    this.index = index;
    this.startX = x;
    this.startWidth = this.widths.getActual(index) || 0;
    this.minWidth = this.widths.getMin(index) || 0;
  }

  public continueResize(x: number) {
    let dx = x - this.startX;
    // Ensure column width stays greater than its minimum width.
    if (this.startWidth + dx < this.minWidth) {
      dx = this.minWidth - this.startWidth;
    }
    this.widths.setActual(this.index, this.startWidth + dx);
    this.widths.updateTotal();
  }

}
